// @ts-check
// Barra de N segmentos (Fase 4a): el panel Progreso del HUD de los editores,
// junto a «X/N desbloqueados». N sale de los datos (61, 60 y 54 a 27 sep
// 2026) y crece con el banco: nunca se escribe a mano.
//
// Decorativa (`aria-hidden`): lo que lee el lector de pantalla es la cifra
// «X/N desbloqueados» que la acompaña.
//
// Momentos de recompensa (Fase 6b, plan de la Fase 3, §3): la barra solo los
// MARCA y el CSS los anima. `momento: 'sube'` (se ha abierto un nivel: el
// segmento `recien` sube y brilla) o `'ola'` (ya están todos: se encienden en
// ola, una vez). Cada segmento sabe su puesto (`--segmento-orden`) para la ola.
import { createSafeElement } from '../../security/safe-dom.js';
import './barra-segmentos.css';

/**
 * @typedef {Object} DatosBarraSegmentos
 * @property {number} total - Número de segmentos (ejercicios del bloque).
 * @property {number} encendidos - Cuántos se encienden, desde el primero.
 * @property {number | null} [recien] - Índice (desde 0) del segmento recién
 *   encendido, que se resalta. Se ignora si no está encendido.
 * @property {'sube' | 'ola' | null} [momento] - El momento de recompensa que
 *   anima el CSS; sin él, la barra se pinta quieta.
 */

/**
 * Entero no negativo; cualquier otra cosa (NaN, negativo, texto) cuenta como 0.
 * @param {number} valor
 */
function entero(valor) {
  return Number.isFinite(valor) && valor > 0 ? Math.floor(valor) : 0;
}

/**
 * @param {DatosBarraSegmentos} datos
 * @returns {{ elemento: HTMLElement, actualizar: (datos: DatosBarraSegmentos) => void }}
 */
export function crearBarraSegmentos(datos) {
  const elemento = createSafeElement('div', { className: 'ui-barra-segmentos' });
  elemento.setAttribute('aria-hidden', 'true');

  /** @param {DatosBarraSegmentos} nuevos */
  const actualizar = ({ total, encendidos, recien = null, momento = null }) => {
    const n = entero(total);
    const e = Math.min(entero(encendidos), n);
    const segmentos = [];
    for (let i = 0; i < n; i += 1) {
      const segmento = document.createElement('span');
      segmento.className = 'ui-barra-segmento';
      // Desde el CSSOM, no con un atributo `style`: la política de seguridad
      // no lo bloquea.
      segmento.style.setProperty('--segmento-orden', String(i));
      if (i < e) segmento.dataset.encendido = 'true';
      if (i === recien && i < e) segmento.dataset.recien = 'true';
      segmentos.push(segmento);
    }
    // Segmentos NUEVOS en cada pintada: su animación arranca al entrar, sin
    // tener que reiniciar nada.
    elemento.replaceChildren(...segmentos);
    elemento.style.setProperty('--segmentos-total', String(Math.max(n, 1)));
    elemento.dataset.total = String(n);
    elemento.dataset.encendidos = String(e);
    if (momento) elemento.dataset.momento = momento;
    else delete elemento.dataset.momento;
  };

  actualizar(datos);
  return { elemento, actualizar };
}
